import { SERVICE_MODES, type ServiceMode } from '../enums';
import type { ReportSpecificFilter, ReportTableColumn } from '../report-runtime';

export interface DailyDriverServiceRow {
  provider_id: string;
  provider_code: string;
  provider_name: string;
  business_date: string;
  date_basis: string;
  agent_id: string;
  agent_code: string | null;
  agent_name: string;
  agent_phone: string;
  service_area_ids: string[];
  service_area_names: string[];
  service_group_ids: string[];
  service_group_names: string[];
  scope_provenance: string;
  attributed_trip_count: number;
  assignment_evidence_trip_count: number;
  completed_trip_count: number;
  cancelled_trip_count: number;
  missed_trip_count: number;
  offers_count: number;
  accepted_offer_count: number;
  rejected_offer_count: number;
  expired_offer_count: number;
  superseded_offer_count: number;
  cancelled_offer_count: number;
  pending_offer_count: number;
  planned_distance_km_sum: number | null;
  planned_distance_known_trip_count: number;
  actual_distance_km_sum: number | null;
  actual_distance_known_trip_count: number;
  planned_duration_min_sum: number | null;
  planned_duration_known_trip_count: number;
  actual_duration_min_sum: number | null;
  actual_duration_known_trip_count: number;
  attribution_basis: string;
  currency: string;
  fare_total_sum: number;
  billed_total_sum: number;
  received_total_sum: number;
  outstanding_total_sum: number;
  free_trip_count: number;
  finance_review_count: number;
  missing_fare_count: number;
}
export interface DailyDriverServiceFilters {
  serviceMode?: ServiceMode[];
  isFree?: boolean;
  agentIds?: string[];
}
export const dailyDriverServiceSpecificFilters = [
  { id: 'serviceMode', type: 'multi', values: SERVICE_MODES },
  { id: 'isFree', type: 'boolean' },
  { id: 'agentIds', type: 'uuidMulti' },
] as const satisfies readonly ReportSpecificFilter[];
export const dailyDriverServiceColumns = [
  {
    id: 'provider_id',
    labelKey: 'dailyDriver_provider_id',
    type: 'text',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_none',
  },
  {
    id: 'provider_code',
    labelKey: 'dailyDriver_provider_code',
    type: 'text',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_none',
  },
  {
    id: 'provider_name',
    labelKey: 'dailyDriver_provider_name',
    type: 'text',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_none',
  },
  {
    id: 'business_date',
    labelKey: 'dailyDriver_business_date',
    type: 'date',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_none',
    meaningKey: 'dailyDriver_dateBasis',
    sortable: true,
    sortKey: 'businessDate',
  },
  {
    id: 'date_basis',
    labelKey: 'dailyDriver_date_basis',
    type: 'text',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_none',
    meaningKey: 'dailyDriver_dateBasis',
  },
  {
    id: 'agent_id',
    labelKey: 'dailyDriver_agent_id',
    type: 'text',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_none',
    pii: true,
  },
  {
    id: 'agent_code',
    labelKey: 'dailyDriver_agent_code',
    type: 'text',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_unknown',
    pii: true,
  },
  {
    id: 'agent_name',
    labelKey: 'dailyDriver_agent_name',
    type: 'text',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_none',
    pii: true,
    sortable: true,
    sortKey: 'agentName',
  },
  {
    id: 'agent_phone',
    labelKey: 'dailyDriver_agent_phone',
    type: 'text',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_none',
    pii: true,
  },
  {
    id: 'service_area_ids',
    labelKey: 'dailyDriver_service_area_ids',
    type: 'list',
    source: 'dailyDriver_source_mapping',
    emptyMeaning: 'dailyDriver_empty_none',
  },
  {
    id: 'service_area_names',
    labelKey: 'dailyDriver_service_area_names',
    type: 'list',
    source: 'dailyDriver_source_mapping',
    emptyMeaning: 'dailyDriver_empty_none',
  },
  {
    id: 'service_group_ids',
    labelKey: 'dailyDriver_service_group_ids',
    type: 'list',
    source: 'dailyDriver_source_mapping',
    emptyMeaning: 'dailyDriver_empty_none',
  },
  {
    id: 'service_group_names',
    labelKey: 'dailyDriver_service_group_names',
    type: 'list',
    source: 'dailyDriver_source_mapping',
    emptyMeaning: 'dailyDriver_empty_none',
  },
  {
    id: 'scope_provenance',
    labelKey: 'dailyDriver_scope_provenance',
    type: 'text',
    source: 'dailyDriver_source_mapping',
    emptyMeaning: 'dailyDriver_empty_none',
    meaningKey: 'dailyDriver_source_mapping',
  },
  {
    id: 'attributed_trip_count',
    labelKey: 'dailyDriver_attributed_trip_count',
    type: 'number',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_zero',
    meaningKey: 'dailyDriver_meaning_attributed',
    unitKey: 'unit_count',
  },
  {
    id: 'assignment_evidence_trip_count',
    labelKey: 'dailyDriver_assignment_evidence_trip_count',
    type: 'number',
    source: 'dailyDriver_source_offers',
    emptyMeaning: 'dailyDriver_empty_zero',
    meaningKey: 'dailyDriver_meaning_evidence',
    unitKey: 'unit_count',
  },
  {
    id: 'completed_trip_count',
    labelKey: 'dailyDriver_completed_trip_count',
    type: 'number',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_zero',
    meaningKey: 'dailyDriver_meaning_completed',
    unitKey: 'unit_count',
  },
  {
    id: 'cancelled_trip_count',
    labelKey: 'dailyDriver_cancelled_trip_count',
    type: 'number',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_zero',
    meaningKey: 'dailyDriver_meaning_terminal',
    unitKey: 'unit_count',
  },
  {
    id: 'missed_trip_count',
    labelKey: 'dailyDriver_missed_trip_count',
    type: 'number',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_zero',
    meaningKey: 'dailyDriver_meaning_terminal',
    unitKey: 'unit_count',
  },
  {
    id: 'offers_count',
    labelKey: 'dailyDriver_offers_count',
    type: 'number',
    source: 'dailyDriver_source_offers',
    emptyMeaning: 'dailyDriver_empty_zero',
    unitKey: 'unit_count',
  },
  {
    id: 'accepted_offer_count',
    labelKey: 'dailyDriver_accepted_offer_count',
    type: 'number',
    source: 'dailyDriver_source_offers',
    emptyMeaning: 'dailyDriver_empty_zero',
    unitKey: 'unit_count',
  },
  {
    id: 'rejected_offer_count',
    labelKey: 'dailyDriver_rejected_offer_count',
    type: 'number',
    source: 'dailyDriver_source_offers',
    emptyMeaning: 'dailyDriver_empty_zero',
    unitKey: 'unit_count',
  },
  {
    id: 'expired_offer_count',
    labelKey: 'dailyDriver_expired_offer_count',
    type: 'number',
    source: 'dailyDriver_source_offers',
    emptyMeaning: 'dailyDriver_empty_zero',
    unitKey: 'unit_count',
  },
  {
    id: 'superseded_offer_count',
    labelKey: 'dailyDriver_superseded_offer_count',
    type: 'number',
    source: 'dailyDriver_source_offers',
    emptyMeaning: 'dailyDriver_empty_zero',
    unitKey: 'unit_count',
  },
  {
    id: 'cancelled_offer_count',
    labelKey: 'dailyDriver_cancelled_offer_count',
    type: 'number',
    source: 'dailyDriver_source_offers',
    emptyMeaning: 'dailyDriver_empty_zero',
    unitKey: 'unit_count',
  },
  {
    id: 'pending_offer_count',
    labelKey: 'dailyDriver_pending_offer_count',
    type: 'number',
    source: 'dailyDriver_source_offers',
    emptyMeaning: 'dailyDriver_empty_zero',
    unitKey: 'unit_count',
  },
  {
    id: 'planned_distance_km_sum',
    labelKey: 'dailyDriver_planned_distance_km_sum',
    type: 'distance',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_unknown',
    meaningKey: 'dailyDriver_meaning_metrics',
  },
  {
    id: 'planned_distance_known_trip_count',
    labelKey: 'dailyDriver_planned_distance_known_trip_count',
    type: 'number',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_zero',
    meaningKey: 'dailyDriver_meaning_metrics',
    unitKey: 'unit_count',
  },
  {
    id: 'actual_distance_km_sum',
    labelKey: 'dailyDriver_actual_distance_km_sum',
    type: 'distance',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_unknown',
    meaningKey: 'dailyDriver_meaning_metrics',
  },
  {
    id: 'actual_distance_known_trip_count',
    labelKey: 'dailyDriver_actual_distance_known_trip_count',
    type: 'number',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_zero',
    meaningKey: 'dailyDriver_meaning_metrics',
    unitKey: 'unit_count',
  },
  {
    id: 'planned_duration_min_sum',
    labelKey: 'dailyDriver_planned_duration_min_sum',
    type: 'number',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_unknown',
    meaningKey: 'dailyDriver_meaning_metrics',
    unitKey: 'unit_minutes',
  },
  {
    id: 'planned_duration_known_trip_count',
    labelKey: 'dailyDriver_planned_duration_known_trip_count',
    type: 'number',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_zero',
    meaningKey: 'dailyDriver_meaning_metrics',
    unitKey: 'unit_count',
  },
  {
    id: 'actual_duration_min_sum',
    labelKey: 'dailyDriver_actual_duration_min_sum',
    type: 'number',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_unknown',
    meaningKey: 'dailyDriver_meaning_metrics',
    unitKey: 'unit_minutes',
  },
  {
    id: 'actual_duration_known_trip_count',
    labelKey: 'dailyDriver_actual_duration_known_trip_count',
    type: 'number',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_zero',
    meaningKey: 'dailyDriver_meaning_metrics',
    unitKey: 'unit_count',
  },
  {
    id: 'attribution_basis',
    labelKey: 'dailyDriver_attribution_basis',
    type: 'text',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_none',
    meaningKey: 'dailyDriver_meaning_attributed',
  },
  {
    id: 'currency',
    labelKey: 'dailyDriver_currency',
    type: 'text',
    source: 'dailyDriver_source_current',
    emptyMeaning: 'dailyDriver_empty_none',
  },
  {
    id: 'fare_total_sum',
    labelKey: 'dailyDriver_fare_total_sum',
    type: 'money',
    source: 'dailyDriver_source_finance',
    emptyMeaning: 'dailyDriver_empty_zero',
  },
  {
    id: 'billed_total_sum',
    labelKey: 'dailyDriver_billed_total_sum',
    type: 'money',
    source: 'dailyDriver_source_finance',
    emptyMeaning: 'dailyDriver_empty_zero',
  },
  {
    id: 'received_total_sum',
    labelKey: 'dailyDriver_received_total_sum',
    type: 'money',
    source: 'dailyDriver_source_finance',
    emptyMeaning: 'dailyDriver_empty_zero',
  },
  {
    id: 'outstanding_total_sum',
    labelKey: 'dailyDriver_outstanding_total_sum',
    type: 'money',
    source: 'dailyDriver_source_finance',
    emptyMeaning: 'dailyDriver_empty_zero',
  },
  {
    id: 'free_trip_count',
    labelKey: 'dailyDriver_free_trip_count',
    type: 'number',
    source: 'dailyDriver_source_finance',
    emptyMeaning: 'dailyDriver_empty_zero',
    unitKey: 'unit_count',
  },
  {
    id: 'finance_review_count',
    labelKey: 'dailyDriver_finance_review_count',
    type: 'number',
    source: 'dailyDriver_source_finance',
    emptyMeaning: 'dailyDriver_empty_zero',
    unitKey: 'unit_count',
  },
  {
    id: 'missing_fare_count',
    labelKey: 'dailyDriver_missing_fare_count',
    type: 'number',
    source: 'dailyDriver_source_finance',
    emptyMeaning: 'dailyDriver_empty_zero',
    unitKey: 'unit_count',
  },
] as const satisfies readonly ReportTableColumn[];
export const dailyDriverServiceDefaultColumns = [
  'provider_name',
  'business_date',
  'agent_name',
  'attributed_trip_count',
  'assignment_evidence_trip_count',
  'offers_count',
  'completed_trip_count',
  'actual_distance_km_sum',
  'actual_distance_known_trip_count',
  'billed_total_sum',
  'received_total_sum',
  'outstanding_total_sum',
] as const;
